/**
 * Match a Color option name to product media when variants have no featured_media.
 * TSW catalog mockups are named by color (…_Navy.jpg) but not attached to variants.
 */
const RESERVED_STEM_TOKENS = new Set([
  'front',
  'back',
  'left',
  'right',
  'side',
  'detail',
  'closeup',
  'lifestyle',
  'jpg',
  'jpeg',
  'png',
  'webp',
  'cc',
  'bc',
  'nl',
  'g5000',
  'g180',
  'g2000',
  'youth',
]);

function tokensFromName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/grey/g, 'gray')
    .match(/[a-z0-9]+/g) || [];
}

function filenameStem(src) {
  const file = String(src || '').split('?')[0].split('/').pop() || '';
  return file.replace(/\.[a-z0-9]+$/i, '');
}

function isUuidToken(token) {
  return /^[a-f0-9]{8,}$/i.test(token) || /^[a-f0-9]{4}$/i.test(token);
}

function mediaTokens(media) {
  const stem = filenameStem(media?.src || media?.preview_src || '');
  const alt = media?.alt || '';
  const raw = `${stem} ${alt}`;
  return tokensFromName(raw).filter((token) => !isUuidToken(token) && !RESERVED_STEM_TOKENS.has(token));
}

function suffixScore(media, colorName) {
  const colorTokens = tokensFromName(colorName);
  if (!colorTokens.length) return 0;

  const colorKey = colorTokens.join('');
  const haystack = mediaTokens(media);
  if (!haystack.length) return 0;

  for (let i = 0; i < haystack.length; i++) {
    const suffix = haystack.slice(i).join('');
    if (suffix === colorKey) {
      return 90 + colorTokens.length;
    }
  }

  return 0;
}

function matchMediaForColor(colorName, mediaItems, allColorNames = []) {
  const items = Array.isArray(mediaItems) ? mediaItems : [];
  const colors = Array.isArray(allColorNames) && allColorNames.length ? allColorNames : [colorName];
  let best = null;
  let bestScore = 0;

  for (const item of items) {
    const score = suffixScore(item, colorName);
    if (!score) continue;

    const claimedByLongerColor = colors.some((other) => {
      if (!other || other === colorName) return false;
      return suffixScore(item, other) > score;
    });
    if (claimedByLongerColor) continue;

    if (score > bestScore) {
      bestScore = score;
      best = item;
    }
  }

  return bestScore >= 80 ? best : null;
}

function getSelectedColorName(root) {
  if (!root) return null;
  const groups = root.querySelectorAll('[data-option-name]');
  for (const group of groups) {
    const name = (group.dataset.optionName || '').toLowerCase();
    if (!name.includes('color') && !name.includes('colour')) continue;
    const checked = group.querySelector('input:checked') || group.querySelector('option[selected]');
    if (checked?.value) return checked.value;
  }
  return null;
}

function extractRawMediaId(galleryMediaId) {
  const value = String(galleryMediaId ?? '');
  if (!value) return '';
  const match = value.match(/-(\d+)$/);
  return match ? match[1] : value;
}

function findColorForMedia(galleryMediaId, map) {
  if (!map || typeof map !== 'object') return null;
  const raw = extractRawMediaId(galleryMediaId);
  if (!raw) return null;
  return map[String(raw)] || map[String(galleryMediaId)] || null;
}

function colorOptionAlreadySelected(currentOptionValueId, mapped) {
  if (!mapped?.optionValueId) return true;
  return String(mapped.optionValueId) === String(currentOptionValueId ?? '');
}

globalThis.TSWColorVariantMedia = {
  matchMediaForColor,
  suffixScore,
  getSelectedColorName,
  filenameStem,
  tokensFromName,
  extractRawMediaId,
  findColorForMedia,
  colorOptionAlreadySelected,
};
